import monthlyLogoUrl from "./assets/svg/monthly-logo.svg?url";

// Pre-load the logo image
let logoImage: HTMLImageElement | null = null;
let logoLoading: Promise<HTMLImageElement> | null = null;

const loadLogo = (): Promise<HTMLImageElement> => {
  if (logoImage) return Promise.resolve(logoImage);
  if (logoLoading) return logoLoading;

  logoLoading = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      logoImage = img;
      resolve(img);
    };
    img.onerror = reject;
    img.src = monthlyLogoUrl as string;
  });

  return logoLoading;
};

// Draw watermark on a canvas context
const drawWatermark = (
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  logo: HTMLImageElement,
  scale: number = 1,
): void => {
  const logoHeight = 18 * scale;
  const logoWidth = (logo.width / logo.height) * logoHeight;
  const padding = 16 * scale;
  const fontSize = 11 * scale;
  const gap = 4 * scale;

  ctx.font = `${fontSize}px "Open Sans", Arial, sans-serif`;
  ctx.fillStyle = "#9CA3AF";
  const poweredByText = "Powered by";
  const textMetrics = ctx.measureText(poweredByText);
  const textWidth = textMetrics.width;

  const totalWidth = textWidth + gap + logoWidth;
  const x = canvasWidth - padding - totalWidth;
  const y = canvasHeight - padding - logoHeight;

  ctx.textBaseline = "middle";
  ctx.fillText(poweredByText, x, y + logoHeight / 2);
  ctx.drawImage(logo, x + textWidth + gap, y, logoWidth, logoHeight);
};

// Add watermark to canvas and download (for Canvas-based charts like Sankey)
export const downloadCanvasWithWatermark = async (
  canvas: HTMLCanvasElement,
  filename: string = "chart",
): Promise<void> => {
  const logo = await loadLogo();

  // Create a new canvas with watermark
  const exportCanvas = document.createElement("canvas");
  const ctx = exportCanvas.getContext("2d");
  if (!ctx) return;

  // Get device pixel ratio for proper scaling
  const dpr = window.devicePixelRatio || 1;

  exportCanvas.width = canvas.width;
  exportCanvas.height = canvas.height;

  // Draw white background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

  // Draw original chart
  ctx.drawImage(canvas, 0, 0);

  // Draw watermark (scale based on canvas DPR)
  drawWatermark(ctx, exportCanvas.width, exportCanvas.height, logo, dpr);

  // Download
  const link = document.createElement("a");
  link.download = `${filename}.png`;
  link.href = exportCanvas.toDataURL("image/png", 1.0);
  link.click();
};

// SVG generation for Highcharts export
export const getWatermarkSVG = (): string => {
  return `
    <g class="watermark" transform="translate(0, 0)">
      <text 
        x="100%" 
        y="100%" 
        text-anchor="end" 
        fill="#9CA3AF" 
        font-size="11" 
        font-family="Open Sans, sans-serif"
        dx="-16"
        dy="-16"
      >Powered by Monthly</text>
    </g>
  `;
};

// Helper to add watermark to exported Highcharts images
export const addWatermarkToHighchartsExport = async (
  // biome-ignore lint/suspicious/noExplicitAny: Highcharts chart instance type is complex and from external library
  chart: any,
  filename: string = "chart",
): Promise<void> => {
  // Pre-load logo
  const logo = await loadLogo().catch(() => null);

  // Get the SVG element with proper dimensions
  const svg = chart.getSVG({
    chart: {
      backgroundColor: "#FFFFFF",
    },
  });

  // Create canvas from SVG
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const img = new Image();
  const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  img.onload = async () => {
    // Use 2x scale for better quality
    const scale = 2;
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;

    // Scale the context for high DPI
    ctx.scale(scale, scale);

    // Draw white background
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, img.width, img.height);

    // Draw the chart
    ctx.drawImage(img, 0, 0, img.width, img.height);

    URL.revokeObjectURL(url);

    // Reset scale for watermark
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Add watermark if logo loaded
    if (logo) {
      drawWatermark(ctx, canvas.width, canvas.height, logo, scale);
    }

    // Download
    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png", 1.0);
    link.click();
  };

  img.src = url;
};
